import {
  TERMINOLOGY_DEFAULTS,
  type TerminologyTermKey,
} from '@attendance/shared/terminology';
import i18n from './i18n.js';

export type Terms = Record<TerminologyTermKey, string>;

/** Fallback terms used before branding loads (the HR/default word set). */
export const DEFAULT_TERMS: Terms = TERMINOLOGY_DEFAULTS.hr;

/**
 * Prefix a noun with the correct English indefinite article ("a" / "an").
 *
 * Copy that interpolates a term can't hard-code the article: terminology is
 * tenant-configurable, so the same sentence has to read "a worker", "an
 * employee" and "an operative". Hard-coding "a" is what produced
 * "Select a employee…".
 */
export function withArticle(noun: string): string {
  return `${/^[aeiou]/i.test(noun) ? 'an' : 'a'} ${noun}`;
}

/**
 * Every English word the two built-in profiles ship. A term NOT in this set was
 * typed by the tenant, and a tenant's own word is their brand — it renders
 * verbatim in every language and must never be translated.
 */
const BUILT_IN_TERMS = new Set<string>(
  Object.values(TERMINOLOGY_DEFAULTS).flatMap((set) => Object.values(set))
);

/** `Sites & Camps` → `sites_camps`, so a term can key an i18n string. */
function termSlug(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
}

/**
 * Localise a term that nobody chose; pass a tenant's own word straight through.
 *
 * Terminology is stored as English (`TERMINOLOGY_DEFAULTS`) and interpolated
 * into copy all over the app, so under Arabic every one of those sentences read
 * half-translated — "Site الرئيسي", "حضور Team". Keying on the ENGLISH DEFAULT
 * rather than the term key is deliberate: the two profiles disagree on the same
 * key (`worksite` is "Worksite" for HR and "Site" for construction, `team` is
 * "Team" vs "Crew"), and those can want different Arabic.
 */
export function localiseTerm(
  value: string,
  t: (key: string, fallback: string) => string
): string {
  if (!BUILT_IN_TERMS.has(value)) return value;
  return t(`terminology.${termSlug(value)}`, value);
}

/** Apply `localiseTerm` across a whole term set. */
export function localiseTerms(
  terms: Terms,
  t: (key: string, fallback: string) => string
): Terms {
  const out = { ...terms };
  (Object.keys(out) as TerminologyTermKey[]).forEach((k) => {
    out[k] = localiseTerm(out[k], t);
  });
  return out;
}

/**
 * Build the i18n override bundle for the navigation labels from a term set.
 * Only the nav keys that map to a configurable noun are overridden; everything
 * else keeps its base translation.
 *
 * A term the tenant did NOT customise is skipped entirely, so the nav keeps its
 * own translation. Overriding with the built-in English word would otherwise
 * overwrite a perfectly good Arabic nav label with "Employees" — and the
 * concatenations below are English word order besides, so they only make sense
 * for a custom word in the first place.
 */
function navOverrides(terms: Terms): { nav: Record<string, string> } {
  const nav: Record<string, string> = {};
  if (!BUILT_IN_TERMS.has(terms.employees)) {
    nav.employees = terms.employees;
    nav.bulkImport = `${terms.employees} import`;
  }
  if (!BUILT_IN_TERMS.has(terms.worksites)) nav.worksites = terms.worksites;
  if (!BUILT_IN_TERMS.has(terms.team)) {
    nav.teamDirectory = `${terms.team} directory`;
    nav.teamAttendance = `${terms.team} attendance`;
  }
  return { nav };
}

// Remember the active terms so we can re-apply them when the language changes
// (switching language re-reads the base bundle, dropping our overrides).
let activeTerms: Terms | null = null;

function applyToActiveLanguage(): void {
  if (!activeTerms) return;
  // deepMerge=true, overwrite=true → replace just these leaf keys.
  i18n.addResourceBundle(i18n.language, 'common', navOverrides(activeTerms), true, true);
}

i18n.on('languageChanged', applyToActiveLanguage);

/**
 * Apply a tenant's terminology to the live i18n instance so navigation renders
 * the chosen wording. Called whenever public branding loads or refreshes.
 */
export function applyTerminology(terms: Terms | undefined | null): void {
  activeTerms = terms ?? DEFAULT_TERMS;
  applyToActiveLanguage();
}
